import { useState, FC, forwardRef } from 'react';

import { Popover } from 'react-tiny-popover';
import { useTheme } from 'styled-components';

import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import ErrorMessage from '@components/ErrorMessage';
import { SearchBarInputProps } from '@components/ui/search/SearchBarInput/SearchBarInputInterface';
import {
  RenderOverlayContentWrapper,
  StyledSearchBarWrapper,
} from '@components/ui/search/SearchBarInput/SearchBarInputStyle';
import useMountedRef from '@hooks/useMountedRef';

import SearchBarItem from './searchBarItem';

export interface CustomPopoverChildComponentProps
  extends React.ComponentPropsWithoutRef<'div'> {
  onClick(): void;
}

const CustomPopoverChildComponent = forwardRef<
  HTMLDivElement,
  CustomPopoverChildComponentProps
>((props, ref) => (
  <StyledSearchBarWrapper ref={ref} onClick={props.onClick}>
    {props.children}
  </StyledSearchBarWrapper>
));

const ShopAndFlySearchBarInput: FC<SearchBarInputProps> = ({
  InputComponent,
  renderOverlayContent,
  error = false,
  errorMessage = '',
  hasAnimationOnError,
  tourStyle,
  tourListing,
  popoverProps: {
    //defaultIsOpen and isOpen should not be set together.
    isOpen, //controlled popover
    defaultIsOpen, //un-controlled popover
    padding = 10,
    onVisibleChange,
    onClickOutside,
    ...otherPopoverProps
  } = {},
}) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(!!defaultIsOpen);
  const theme = useTheme();

  const { parentRef, isParentReady } = useMountedRef<HTMLDivElement>();
  const isControlledPopover = typeof isOpen === 'boolean';

  const closeOverlay = () => {
    isControlledPopover ? onVisibleChange?.(false) : setIsOverlayOpen(false);
  };

  const toggleOverlay = () => {
    isControlledPopover
      ? setTimeout(() => onVisibleChange?.(!isOpen))
      : setTimeout(() => setIsOverlayOpen(!isOverlayOpen));
  };

  const isOverlayVisible = isControlledPopover ? !!isOpen : isOverlayOpen;

  return (
    <div ref={parentRef}>
      {parentRef?.current && isParentReady ? (
        <Popover
          positions={['bottom', 'left']}
          align='start'
          padding={padding}
          containerStyle={{
            zIndex: `${theme.zIndices.searchBar}`,
            backgroundColor: 'white',
            width: tourListing ? '818px' : tourStyle ? '1098px' : '540px',
            display: 'flex',
            justifyContent: 'center',
            margin: '0px',
            boxShadow: '0 20px 40px 0 rgba(0, 0, 0, 0.2)',
          }}
          reposition={false}
          parentElement={parentRef.current}
          content={() => {
            return renderOverlayContent ? (
              <RenderOverlayContentWrapper>
                {renderOverlayContent(closeOverlay)}
              </RenderOverlayContentWrapper>
            ) : (
              <div /> //TODO: it will never render, ts null error fix
            );
          }}
          isOpen={isOverlayVisible}
          onClickOutside={(e) => {
            onClickOutside?.(e);
            closeOverlay();
          }}
          {...otherPopoverProps}
        >
          <CustomPopoverChildComponent onClick={toggleOverlay}>
            <LocationAnimationErrorBox
              hasError={error}
              hasAnimationOnError={hasAnimationOnError}
            >
              <SearchBarItem InputComponent={InputComponent} />
            </LocationAnimationErrorBox>
            {error && errorMessage ? (
              <ErrorMessage message={errorMessage} showIcon={false} />
            ) : null}
          </CustomPopoverChildComponent>
        </Popover>
      ) : null}
    </div>
  );
};

export default ShopAndFlySearchBarInput;
